<template>
  <div>
    <vx-card class="mb-5">
      <heading size="h2" style-size="h4" class="mb-3">Life in the Police Academy</heading>
      <p class="mb-5">
        Once your application to the police force is successful, you are going to end up at the
        Police Academy in your state. The following video gives you a small insight into what you
        can expect.
      </p>
      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
          <custom-video
            aria-label="Watch life in the Police academy video"
            elem-id="video-btn-0"
            :video-image="intro_videos[1].image"
            :video-url="intro_videos[1].video"
          />
        </div>
      </div>

      <heading size="h2" style-size="h4" class="mb-3">
        A Day in the Life of a Police Officer:
      </heading>
      <p class="mb-5">
        As is mentioned in the video, there’s no such thing as a typical day at work when working as
        a police officer, but the following video will give you an insight into some of the
        possibilities for a day’s work.
      </p>
      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
          <custom-video
            aria-label="Watch a day in the Life of a Police Officer video"
            elem-id="video-btn-1"
            :video-image="intro_videos[2].image"
            :video-url="intro_videos[2].video"
          />
        </div>
      </div>

      <heading size="h2" style-size="h4" class="mb-3">Australian Federal Police</heading>
      <p class="mb-5">Wonder what an Australian Federal Police Officer does? Find out here:</p>
      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
          <custom-video
            aria-label="Watch Australian Federal Police video"
            elem-id="video-btn-2"
            :video-image="intro_videos[3].image"
            :video-url="intro_videos[3].video"
          />
        </div>
      </div>
    </vx-card>

    <vx-card class="mt-base mb-5">
      <heading size="h2" style-size="h4" class="mb-3"> Specialist Roles</heading>
      <accordion-panel v-for="(role, id) in specialist_roles" :key="id" :aria-title="role.title">
        <template v-slot:title>
          <div>
            {{ role.title }}
          </div>
        </template>
        <template v-slot:content>
          <img :src="role.image" class="img-responsive" alt="" />
          <div class="content" role="presentation" alt="" v-html="role.content"></div>
        </template>
      </accordion-panel>

      <p class="mb-5">
        Sourced from
        <a href="https://www.police.nsw.gov.au" target="_blank">www.police.nsw.gov.au</a> 2021.
      </p>

      <p class="mb-5">
        <u>Remember:</u> This is an example of one state. You can also explore the specialist areas
        in other states and territories if you would like to. Just Click on the logos below to visit
        each Police Force website.
      </p>

      <vs-row vs-align="center" vs-type="flex" vs-justify="center" vs-w="12" class="my-5">
        <vs-col
          :key="index"
          v-for="(col, index) in state_images"
          vs-type="flex"
          vs-justify="center"
          vs-align="center"
          vs-lg="3"
          vs-sm="4"
          vs-xs="6"
        >
          <a :href="col.url" target="_blank"
            ><img :src="col.image" class="img-responsive pb-10" :alt="col.alt"
          /></a>
        </vs-col>
      </vs-row>
    </vx-card>
    <vx-card title="Observation Skills">
      <p class="mb-5">
        <u
          >As a member of any unit in the police force you must learn and develop unbiased
          Observation Skills!</u
        >
      </p>
      <p class="mb-5">
        You can start developing these skills any time and they can be just as important to law
        enforcement as a public citizen. You could start by becoming a Neighbourhood Watch
        participant.
      </p>
      <p class="mb-5">Below is an example of how this is done in Western Australia.</p>
      <p class="mb-5">
        The Western Australia Police Force has a provided information and training scenarios on the
        importance of observation as a community member and why it is highly valued.
      </p>
      <p class="mb-5">There are 8 Training Scenarios:</p>
      <vs-row
        vs-align="center"
        vs-type="flex"
        vs-justify="center"
        vs-w="12"
        class="my-5"
        :key="index"
        v-for="(col, index) in observation_skills"
      >
        <vs-col vs-type="flex" vs-align="center" vs-lg="6" vs-sm="8" vs-xs="12">
          <h5>
            <strong>Scenario {{ index + 1 }}</strong> - {{ col.title }}
          </h5>
        </vs-col>
        <vs-col vs-type="flex" vs-align="center" vs-lg="6" vs-sm="4" vs-xs="12">
          <custom-video
            :aria-label="`Watch Scenario ${index + 1} - ${col.title} video`"
            :elem-id="`observation-skills-video-${index + 1}`"
            :video-image="col.image"
            :video-url="col.video"
          />
        </vs-col>
      </vs-row>

      <p class="mb-5">
        The Western Australia Police Force website also gives you these Observation Tips:
      </p>
      <h4 class="mb-5">Person</h4>
      <p class="mb-5">
        Start from the top and work down. The face contains the most detail and features. If you
        start from their head and work down to their shoes you will gather information in an order
        you can remember. Even if they turn or run away, you may still be able to identify height,
        approximate age, build and clothing.
      </p>
      <h4 class="mb-5">Vehicle</h4>
      <p class="mb-5">
        Start with vehicle registration number. Try to get the driver description and then as the
        car travels away you may still be able to identify the make, model, colour and identifiable
        markings.
      </p>
      <p class="mb-5">
        See an example of an “Incident Report”
        <a
          aria-label="Example of an Incident Report"
          :href="'content/policing/incident_report.pdf' | blob_url"
          target="_blank"
          ><strong>HERE</strong></a
        >.
      </p>
      <p class="mb-5">
        Sourced from the
        <a
          href="https://www.police.wa.gov.au/Our-Community/Neighbourhood-Watch/Observation-and-Reporting-Techniques"
          target="_blank"
          >Western Australia Police Force Our Community.</a
        >
      </p>
    </vx-card>
  </div>
</template>

<script>
import store from '@/store/store';
import CustomVideo from '@/components/CustomVideo.vue';
import AccordionPanel from '@/components/AccordionPanel';

export default {
  components: { CustomVideo, AccordionPanel },
  data() {
    return {
      intro_videos: {
        1: {
          image: require('@/assets/images/experiences/policing/police_academy.jpg'),
          video: 'https://www.youtube.com/embed/KaifkiBzD-A',
        },
        2: {
          image: require('@/assets/images/experiences/policing/day_in_the_life_police_officer.jpg'),
          video: 'https://www.youtube.com/embed/udhTTJXvPmk',
        },
        3: {
          image: require('@/assets/images/experiences/policing/afp.jpg'),
          video: 'https://www.youtube.com/embed/nyS-D0OWqEg',
        },
      },
      specialist_roles: [
        {
          title: 'Aviation Support Branch',
          image: require('@/assets/images/experiences/policing/aviation-support.png'),
          content:
            '<p>The Aviation Support Branch (Air Wing) provides airborne support to the NSW Police Force.</p>' +
            '<p>Today the Aviation Support Branch has 6 Polair aircraft - 5 helicopters and 1 fixed wing. The aircraft are fitted with a mixture of hi-tech thermal imagining equipment, on board computers, real time video feeds to ground stations, search lights and rescue winches.</p>' +
            '<p>The Aviation Support Branch provides a range of services including targeting serious, major and organised crime and counter terrorism matters, crime operations, search and rescue,' +
            'emergencies and the investigation of aviation fatalities across New South Wales.</p>' +
            '<p>To be employed at the Aviation Support Branch you have to be a true all-rounder, possessing a high level of fitness and an ability to lear and utilise state of the art technology, combined with a high level of enthusiasm and your skills as an experienced' +
            'Police Officer.</p>',
        },
        {
          title: 'Bicycle Unit',
          image: require('@/assets/images/experiences/policing/bicycleUnit.png'),
          content:
            '<p>Bicycle Police have been a part of policing for many years and is a popular form of policing due to its high visibility.</p>' +
            '<p>Much of the bicycle patrolling is intelligence based, focusing on street level offences such as drug related matters, property crime, and offences such as assaults.</p>' +
            '<p>To join the Bicycle Squad, officers require a set fitness standard. In addition officers must undertake an intensive, externally accredited training course at the NSW Police Academy to develop and ensure that certain skills and competency levels are met.</p>' +
            '<p>Part of this training incorporates certain aspects of bicycle and equipment maintenance, rules and regulations and strategic considerations. The SW police fleets of mountain bikes are state of the art and designed by experienced Bicycle Squad officers specifically for policing duties.</p>',
        },
        {
          title: 'Dog Unit',
          image: require('@/assets/images/experiences/policing/dogUnit.png'),
          content:
            '<p>The NSW Police Force Dog Unit is part of the State Protection Group and Counter Terrorism and Special Tactics Command. The Unit is the largest Dog Unit of any Police Force in Australia and is available for operations 24 hours a day, seven days a week. The Police Dog Unit has teams based around the State of NSW as far north as Tweed Heads, south to Wagga Wagga and west to Dubbo. The Unit currently deploys teams of English Springer Spaniels, Labradors and German Shepherds trained in:</p>' +
            '<ul><li>Drug Detection</li>' +
            '<li>Genpralpurose or general saro, A luding tacticalsolce dong)</li>' +
            '<li>Firearm and Explosive Detection</li>' +
            '<li>Cadaver Detection</li></ul>' +
            '<p>As dogs have such an excellent sense of smell and degree of' +
            'trainability, the NSW Police Force are continually looking at combating emerging crime trends by training dogs to detect new odours.</p>' +
            '<p>The Unit employs both male and female handlers, most of whom work two dogs. Prospective recruits must have completed three years general policing prior to application. Candidates must then pass a physical barrier test, selection course and Novice Dog Handling Course, prior to working a police dog in the Community. The NSW Police Dog Unit is always looking for fit, hard working and committed Police' +
            'Officers to work in a dynamic work environment. Prior dog handling experience is not necessary.</p>',
        },
        {
          title: 'Detectives',
          image: null,
          content:
            "<p>Criminal Investigators, or Detectives as they are more commonly known, are generally based at Police Area Commands (PAC's) throughout NSW.</p>" +
            "<p>Detectives stationed at PAC's are responsible for a broad range of investigations and require excellent all-round policing skills to investigate the different types of crime. In addition, Detectives also operate out of the State Crime Command (SCC) which is based at" +
            'Police Headquarters, Parramatta.</p>' +
            '<p>Detectives are highly skilled, highly trained investigators who complete an accredited university qualification as part of their employment.</p>',
        },
        {
          title: 'Forensic Evidence & Technical Services Command (FETSC)',
          image: require('@/assets/images/experiences/policing/forensicEvidence.png'),
          content:
            '<p>The Forensic Evidence & Technical Services Command (FETSC) provide a specialised forensic response to crime and incident scenes incorporating the collection and examination of exhibits, and the scientific analysis and interpretation of evidence in accordance with investigative and judicial requirements. </p>' +
            "<p>The FETSC's career options include: crime scene examination, fingerprint identification, document examination, ballistics investigation, forensic imaging and engineering investigation </p>" +
            '<p>To be employed within the Forensic Evidence & Technical Services Command officers must complete three years of general duties policing and then undertake psychometric and aptitude tests before they are further assessed in accordance with selection requirements for each discipline. In addition officers are required to complete relevant tertiary qualifications and achieve expertise status in their chosen forensic field.</p>',
        },
        {
          title: 'Highway Patrol',
          image: require('@/assets/images/experiences/policing/highwayPatrol.png'),
          content:
            "<p>The HWP's goal is to minimise road trauma, promote orderly and safe road use and ensure the free flow of traffic. This is primarily achieved through the delivery of targeted, high profile traffic enforcement activities to deter and detect inappropriate road user behaviours.</p>" +
            '<p>They use a variety of modern equipment to do their job, including radar and lidar speed detection devices, automatic number plate recognition technologies, in-car video devices, mobile data terminals, portable breath and drug screening devices and radio communications gear.</p>' +
            '<p>To become a part of the HWP team, an applicant must undertake tests and on-the-job' +
            'training prior to securing a position.</p>',
        },
        {
          title: 'Marine Area Command',
          image: require('@/assets/images/experiences/policing/marineArea.png'),
          content:
            '<p>The Marine Area Command mission is to pursue excellence in maritime law enforcement and the coordination and control of marine search and rescue. The Command is responsible for preventing and reducing marine crime, the active patrolling of NSWs inland and coastal waterways, intelligence gathering and conducting operations relating to criminal activities such as drug smuggling or importation.</p>' +
            '<p>To become a member of the Marine Area Command a police officer must have three years policing experience, and display the following four attributes:</p>' +
            '<ul><li>Contemporary policing experience, a propensity to work in the marine environment and a high level of fitness.</li>' +
            '<li>Dedicate themselves to training and gain marine qualifications.</li>' +
            '<li>The ability and keenness to detect and arrest offenders breaking the law. We are looking for a demonstrated ability to be pro-active and take action.</li>' +
            '<li>A desire to perform policing functions in state of the art vessels on NSW waters </li></ul>',
        },
        {
          title: 'Mounted Police Unit',
          image: require('@/assets/images/experiences/policing/MountedPolice.png'),
          content:
            '<p>NSW Mounted Police is the oldest continuous Mounted Police unit in the world, having been formed in 1825.</p>' +
            '<p>The unit provides support at public order management events by undertaking crowd control and traffic management. The majority of work is in support of Local Area Commands conducting high visibility patrols and local operations targeting crime. Mounted Police are used for their searching capability to cover rough terrain and have a greater ground scope than foot police. A component of protocol and ceremonial duties sees the horses involved in parades and ceremonies.</p>' +
            "<p>To become a 'Mountie' a physical, medical and riding assessment test is required</p>",
        },
        {
          title: 'Police Transport Command',
          image: require('@/assets/images/experiences/policing/PoliceTransport.png'),
          content:
            "<p>In May 2012, the NSW Police Force assumed full responsibility for the State's transport security. At capacity, the dynamic Police Transport Command will be made up of more than 600 swor officers who will bring major improvements to security across the transport network. The Police Transport Command is responsible for reducing criminal and anti-social behaviour across the transport network by responding to the most up-to-date intelligence and targeting known trouble spots through covert, highly visible and drug detection dog operations. Currently, a member of the NSW Police Force can transfer to the Police Transport Command after 2 years of service </p>",
        },
        {
          title: 'Public Order Riot Squad',
          image: require('@/assets/images/experiences/policing/publicOrder.png'),
          content:
            '<p>The Public Order & Riot Squad (PORS) began operations in January 2006 to ensure the' +
            'NSW Police Force is ready and equipped to resolve any public order incident or situation.</p>' +
            '<p>PORS police are trained and ready to deal with:</p>' +
            '<ul><li>Riots</li>' +
            '<li>Protests</li>' +
            '<li>Demonstrations and events with large crowds</li>' +
            '<li>High visibility policing (HVP) and large operations</li>' +
            '<li>Searches for persons, property, cannabis plantations and around internationally protected persons</li>' +
            '<li>Canvassing of crime scenes</li>' +
            '<li>Staffing of correctional institutions during industrial disputes</li>' +
            '<li>Disaster victim identification (DVI)</li>' +
            '<li>First response to chemical, biological or radiological incidents (CBR)</li></ul>' +
            '<p>To join the Public Order Riot Squad you must meet strict medical, physical and psychological screening and be a currently accredited' +
            'Operations Support Group (OSG) operative</p>',
        },
        {
          title: 'Rescue and Bomb Disposal Unit',
          image: require('@/assets/images/experiences/policing/rescueAndBomb.jpg'),
          content:
            '<p>The Rescue and Bomb Disposal Unit is based in Sydney but with the support of country - based rescue units in Newcastle, Wollongong, Blue Mountains, Bathurst, Goulburn and Lismore and provides around the clock response across NSW.</p>' +
            '<p>RBDU capability covers general land and vertical rescue, bomb appraisal and disposal, search and rescue coordination, and specialist support services such as access to dangerous and difficult to reach places, and support to police negotiators by restraining suicidal persons.</p>' +
            '<p>Three years general duties experience is required before an officer is eligible to apply for' +
            'the Police Rescue Operations Course to become a member of the Unit.</p>',
        },
        {
          title: 'Tactical Operations Unit',
          image: null,
          content:
            '<p>In 1991 the Tactical Operations Unit (TOU) and the Negotiations Unit emerged as a part of the State Protection Group (SPG).</p>' +
            '<p>Though based in Sydney, the SP maintains an around the clock capability across NSW with the support of the part time, regionally based State Protections Support Unit (SPSU) and Police Negotiators.</p>' +
            '<p>The TOU and Negotiations Unit work together, with the primary objective being the safe resolution of high risk situations, without loss of life, injury to persons or damage to property.</p>' +
            "<p>The TOU and the Negotiations Unit seek the most suitable and best possible officers. An officer would need several years' experience in general policing before applying, and would then need to undertake psychometric testing (and for the TOU, a series of medical and fitness assessments) before they are further assessed in accordance with selection parameters for each unit.</p>",
        },
      ],
      observation_skills: [
        {
          title: 'Bus Stop',
          image: require('@/assets/images/experiences/policing/bus_stop.jpg'),
          video: 'https://www.youtube.com/embed/Yko7SWnR8y8?autoplay=1',
        },
        {
          title: 'Construction Site',
          image: require('@/assets/images/experiences/policing/construction_site.jpg'),
          video: 'https://www.youtube.com/embed/toFdJIPUKP4?autoplay=1',
        },
        {
          title: 'Dumping',
          image: require('@/assets/images/experiences/policing/dumping.jpg'),
          video: 'https://www.youtube.com/embed/du4sieYIRhU?autoplay=1',
        },
        {
          title: 'Car Park',
          image: require('@/assets/images/experiences/policing/car_park.jpg'),
          video: 'https://www.youtube.com/embed/IRxBuin1e_g?autoplay=1',
        },
        {
          title: 'Suspicious Activity',
          image: require('@/assets/images/experiences/policing/suspicious_activity.jpg'),
          video: 'https://www.youtube.com/embed/SFAPIGWPJJA?autoplay=1',
        },
        {
          title: 'Suspicious Person',
          image: require('@/assets/images/experiences/policing/suspicious_person.jpg'),
          video: 'https://www.youtube.com/embed/xBWbU6eU5VI?autoplay=1',
        },
        {
          title: 'Suspicious Vehicle at Park',
          image: require('@/assets/images/experiences/policing/suspicious_vehicle_at_park.jpg'),
          video: 'https://www.youtube.com/embed/2TEjrjCm-hs?autoplay=1',
        },
        {
          title: 'Workers',
          image: require('@/assets/images/experiences/policing/workers.jpg'),
          video: 'https://www.youtube.com/embed/Djw7Ly_sDoc?autoplay=1',
        },
      ],
      state_images: [
        {
          url: 'https://www.afp.gov.au/careers',
          image: require('@/assets/images/experiences/policing/afp_logo.png'),
          alt: 'Australian Federal Police careers (opens in a new tab)',
        },
        {
          url: 'https://www.police.nsw.gov.au/recruitment',
          image: require('@/assets/images/experiences/policing/nsw_police_logo.png'),
          alt: 'New South Wales Police Force recruitment (opens in a new tab)',
        },
        {
          url: 'https://www.police.vic.gov.au/careers',
          image: require('@/assets/images/experiences/policing/vic_police_logo.png'),
          alt: 'Victoria Police careers (opens in a new tab)',
        },
        {
          url: 'https://www.police.qld.gov.au/units/police-recruiting',
          image: require('@/assets/images/experiences/policing/qld_police_logo.png'),
          alt: 'Queensland Police recruiting (opens in a new tab)',
        },
        {
          url: 'https://www.police.sa.gov.au/join-us',
          image: require('@/assets/images/experiences/policing/sa_police_logo.png'),
          alt: 'South Australia Police, join us (opens in a new tab)',
        },
        {
          url: 'https://www.letsjoinforces.wa.gov.au',
          image: require('@/assets/images/experiences/policing/wa_police_logo.png'),
          alt: 'Western Australia Police, join forces (opens in a new tab)',
        },
        {
          url: 'https://www.pfes.nt.gov.au/careers-policing',
          image: require('@/assets/images/experiences/policing/nt_police_logo.png'),
          alt: 'Northern Territory Police careers (opens in a new tab)',
        },
        {
          url: 'https://www.police.tas.gov.au',
          image: require('@/assets/images/experiences/policing/tas_police_logo.png'),
          alt: 'Tasmania Police (opens in a new tab)',
        },
      ],
    };
  },
  computed: {
    activeExperienceID() {
      return store.getters['authStudent/activeExperienceID'];
    },
    experience() {
      const loginType = localStorage.getItem('login_type');
      if (loginType == 'student') {
        const experiences = store.getters['authStudent/experiences'];
        return experiences.find((i) => i.id == this.activeExperienceID);
      } else {
        return store.getters['authTeacher/experience'];
      }
    },
  },
  beforeMount() {
    store.commit('authStudent/setActiveExperienceID', +this.$route.params.id);
  },
};
</script>
<style lang="scss">
.role-desc {
  display: flex;
  gap: 20px;
  flex-direction: column;

  img {
    max-width: 100%;
    max-height: 200px;
    object-fit: contain;
  }

  p {
    margin-bottom: 15px;
  }

  ul {
    list-style-type: disc;
    margin-bottom: 30px;
    padding-left: 30px;

    li {
      margin-bottom: 10px;
    }
  }
}
</style>
